import { accountNumber, defineResource, taxBranch } from '@tnn/core'
import { z } from 'zod'
import { bankAccountDigits, bankAccountNumber, banks, partyTypes } from '../party'

export const customerStatuses = ['active', 'inactive'] as const

/** Another account a supplier is paid into: their second bank's, or a contract driver's of a partner who owns the trucks */
export const payeeAccountSchema = z.object({
  name: z.string().trim().min(1).max(120),
  bank: z.enum(banks),
  account: bankAccountDigits,
})
export type PayeeAccount = z.infer<typeof payeeAccountSchema>

/** One of a partner's bank accounts; the main one's `name` may be blank (the partner's own name) */
export type PartnerAccount = Omit<PayeeAccount, 'name'> & { name: string }

/** A partner's bank accounts as one list: their main one (`bank`, `bankAccount`, `bankAccountName`) first, then `payees` */
export function partnerAccounts(partner: Readonly<Record<string, unknown>>): PartnerAccount[] {
  const main =
    partner.bank && partner.bankAccount
      ? [
          {
            bank: partner.bank as PartnerAccount['bank'],
            account: String(partner.bankAccount),
            name: String(partner.bankAccountName ?? ''),
          },
        ]
      : []
  return [...main, ...((partner.payees ?? []) as PayeeAccount[])]
}

/** The partner's fields for a list of accounts (`partnerAccounts`): the first is the main one, the others named (`fallbackName`) */
export function partnerAccountFields(accounts: readonly PartnerAccount[], fallbackName: string) {
  const [main, ...others] = accounts
  return {
    bank: main?.bank ?? null,
    bankAccount: main?.account ?? null,
    bankAccountName: main?.name.trim() || null,
    payees: others.map((a) => ({ bank: a.bank, account: a.account, name: a.name.trim() || fallbackName })),
  }
}

/**
 * accounting.customer (คู่ค้า): a business partner — a customer billed for freight, a supplier the
 * company buys from and pays (บัญชีฝั่งจ่าย), or both (a transport company hiring and hired). The
 * resource keeps its name; what it is to us is `isCustomer` / `isVendor`.
 */
export const customer = defineResource({
  app: 'accounting',
  name: 'customer',
  path: 'customers',
  schema: z.object({
    code: z.string().trim().min(1).max(20),
    /** A company withholds tax on what it pays; an individual cannot (`canWithhold`). Always chosen, never assumed */
    partyType: z.enum(partyTypes),
    name: z.string().trim().min(1).max(200),
    taxId: z
      .string()
      .regex(/^\d{13}$/)
      .nullish(),
    /** Companies: 00000 = head office, otherwise the branch number printed on tax invoices */
    branch: z.string().trim().max(10).nullish(),
    /**
     * The address on tax invoices (ที่อยู่ตามใบกำกับภาษี): house number and road, then the area below. Not always known
     * when a partner is added (the old program had none for some): their documents warn while it is missing
     */
    address: z.string().trim().max(500).nullish(),
    subdistrict: z.string().trim().max(100).nullish(),
    district: z.string().trim().max(100).nullish(),
    province: z.string().trim().max(100).nullish(),
    postcode: z.string().trim().max(10).nullish(),
    contactName: z.string().trim().max(120).nullish(),
    phone: z.string().trim().max(30).nullish(),
    email: z.email().nullish(),
    creditDays: z.number().int().min(0).max(365).default(30),
    isCustomer: z.boolean().default(true),
    isVendor: z.boolean().default(false),
    /** A supplier's account the company pays into */
    bank: z.enum(banks).nullish(),
    bankAccount: bankAccountNumber,
    bankAccountName: z.string().trim().max(120).nullish(),
    /** Its other accounts (a second bank's, a driver's), named by accounting: payments pick one, dispatch only picks for advances */
    payees: z.array(payeeAccountSchema).max(20).default([]),
    /** The withholding rate its bills usually take (a transport sub-contractor 1, a service 3, a landlord 5) */
    defaultWhtRate: z.number().min(0).max(5).nullish(),
    status: z.enum(customerStatuses).default('active'),
    notes: z.string().max(1000).nullish(),
  }),
  fields: {
    code: { search: true, list: { width: 110, priority: 2 }, form: { span: 4 }, import: { aliases: ['รหัส', 'รหัสลูกค้า'] } },
    name: { search: true, list: { priority: 1 }, form: { span: 8 }, import: { aliases: ['ชื่อ', 'ชื่อลูกค้า', 'ชื่อบริษัท'] } },
    partyType: {
      label: 'party.type',
      optionLabels: 'party',
      filter: true,
      list: { width: 130, priority: 2 },
      form: { span: 4 },
      import: { aliases: ['ประเภท', 'ประเภทลูกค้า'] },
    },
    // One partner on the books once: the form names whoever has the tax ID (and branch) or the number already
    taxId: {
      type: 'taxId',
      warnTaken: { with: ['branch'] },
      search: true,
      list: { width: 160, priority: 3 },
      form: { span: 4 },
      import: { aliases: ['เลขประจำตัวผู้เสียภาษี'] },
    },
    // Drawn by the app only for companies
    branch: { list: { hidden: true }, form: { span: 4, custom: true }, import: { aliases: ['สาขา'], value: taxBranch } },
    creditDays: { list: { align: 'end', priority: 3 }, form: { span: 4 } },
    isCustomer: { filter: true, list: { width: 100, priority: 3 }, form: { span: 4, section: 'roles' } },
    isVendor: { filter: true, list: { width: 100, priority: 2 }, form: { span: 4, section: 'roles' } },
    bank: {
      type: 'select',
      options: banks,
      optionLabels: 'banks',
      list: false,
      form: { span: 4, section: 'vendor', custom: true },
    },
    bankAccount: { list: false, form: { span: 4, section: 'vendor', custom: true }, import: { value: accountNumber } },
    bankAccountName: { list: false, form: { span: 4, section: 'vendor', custom: true } },
    defaultWhtRate: { type: 'percent', list: false, form: { span: 4, section: 'vendor', custom: true } },
    payees: { type: 'json', list: false, form: { span: 12, section: 'vendor', custom: true }, import: false, export: false },
    address: { type: 'textarea', list: false, form: { span: 12, section: 'address' }, import: { aliases: ['ที่อยู่'] } },
    // One area picker fills subdistrict, district, province and postcode
    province: {
      filter: true,
      list: { width: 150, priority: 3 },
      form: { span: 12, section: 'address', custom: true },
      import: { aliases: ['จังหวัด'], header: 'form.province' },
    },
    district: { list: false, form: { hidden: true }, import: { aliases: ['อำเภอ', 'เขต'] } },
    subdistrict: { list: false, form: { hidden: true }, import: { aliases: ['ตำบล', 'แขวง'] } },
    postcode: { list: false, form: { hidden: true }, import: { aliases: ['รหัสไปรษณีย์'] } },
    contactName: { suggest: true, list: { priority: 3 }, form: { span: 4, section: 'contact' } },
    phone: {
      type: 'phone',
      warnTaken: true,
      list: { priority: 2 },
      form: { span: 4, section: 'contact' },
      import: { aliases: ['โทร', 'เบอร์โทร'] },
    },
    email: { list: { hidden: true }, form: { span: 4, section: 'contact' } },
    status: { filter: true, tones: { active: 'success', inactive: 'neutral' }, list: { priority: 1 }, form: { span: 4 } },
    notes: { type: 'textarea', form: { span: 12, section: 'other' } },
  },
  title: 'name',
  columns: [
    'code',
    'name',
    'partyType',
    'isCustomer',
    'isVendor',
    'taxId',
    'contactName',
    'phone',
    'creditDays',
    'status',
    'branch',
    'email',
  ],
  sections: ['main', 'roles', 'address', 'contact', 'vendor', 'other'],
  defaultSort: 'code',
  importKey: 'code',
  card: { title: 'name', subtitle: 'contactName', meta: ['code', 'phone'], status: 'status' },
})
